import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Not authenticated — expected on first load; keep silent
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me while returning from Google OAuth; AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("tasklink_token", data.token);
    setUser(data.user);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (err) {
      console.warn("Failed to refresh /me after login:", err?.message);
    }
    return data.user;
  }, []);

  const registerEmail = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("tasklink_token", data.token);
    setUser(data.user);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (err) {
      console.warn("Failed to refresh /me after register:", err?.message);
    }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("Logout request failed:", err?.message); }
    localStorage.removeItem("tasklink_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, loginEmail, registerEmail, logout, checkAuth }),
    [user, loading, loginEmail, registerEmail, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
